import React from 'react';
import { RotateCw, RotateCcw, FlipHorizontal, FlipVertical, Sliders, Sun, Contrast, Palette, Eye } from 'lucide-react';

export interface ImageAdjustmentsState {
  rotation: number; // 0, 90, 180, 270
  flipH: boolean;
  flipV: boolean;
  brightness: number; // 0 - 200, default 100
  contrast: number; // 0 - 200, default 100
  saturation: number; // 0 - 200, default 100
  blur: number; // 0 - 20px, default 0
  grayscale: boolean;
  sepia: boolean;
}

export const INITIAL_ADJUSTMENTS: ImageAdjustmentsState = {
  rotation: 0,
  flipH: false,
  flipV: false,
  brightness: 100,
  contrast: 100,
  saturation: 100,
  blur: 0,
  grayscale: false,
  sepia: false,
};

interface ImageAdjustmentsProps {
  adjustments: ImageAdjustmentsState;
  onChange: (adjustments: ImageAdjustmentsState) => void;
  onReset: () => void;
}

export function ImageAdjustments({ adjustments, onChange, onReset }: ImageAdjustmentsProps) {
  const updateProp = <K extends keyof ImageAdjustmentsState>(key: K, value: ImageAdjustmentsState[K]) => {
    onChange({ ...adjustments, [key]: value });
  };

  const handleRotate = (deg: number) => {
    const nextRot = (adjustments.rotation + deg + 360) % 360;
    updateProp('rotation', nextRot);
  };

  return (
    <div className="space-y-4 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-4 shadow-sm">
      <div className="flex justify-between items-center pb-2 border-b border-slate-100 dark:border-slate-800">
        <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-200 flex items-center gap-2">
          <Sliders className="w-4 h-4 text-indigo-500" />
          Transform & Color Adjustments
        </h3>
        <button
          type="button"
          onClick={onReset}
          className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline font-medium"
        >
          Reset Filters
        </button>
      </div>

      {/* Rotation & Flip Controls */}
      <div className="grid grid-cols-4 gap-2">
        <button
          type="button"
          onClick={() => handleRotate(-90)}
          className="p-2 rounded-lg border border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center justify-center gap-1.5 text-xs font-medium text-slate-700 dark:text-slate-300 transition-colors"
          title="Rotate 90° CCW"
        >
          <RotateCcw className="w-3.5 h-3.5" />
          -90°
        </button>
        <button
          type="button"
          onClick={() => handleRotate(90)}
          className="p-2 rounded-lg border border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center justify-center gap-1.5 text-xs font-medium text-slate-700 dark:text-slate-300 transition-colors"
          title="Rotate 90° CW"
        >
          <RotateCw className="w-3.5 h-3.5" />
          +90°
        </button>
        <button
          type="button"
          onClick={() => updateProp('flipH', !adjustments.flipH)}
          className={`p-2 rounded-lg border flex items-center justify-center gap-1.5 text-xs font-medium transition-colors ${
            adjustments.flipH
              ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300'
              : 'border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300'
          }`}
          title="Flip Horizontal"
        >
          <FlipHorizontal className="w-3.5 h-3.5" />
          Flip H
        </button>
        <button
          type="button"
          onClick={() => updateProp('flipV', !adjustments.flipV)}
          className={`p-2 rounded-lg border flex items-center justify-center gap-1.5 text-xs font-medium transition-colors ${
            adjustments.flipV
              ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300'
              : 'border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300'
          }`}
          title="Flip Vertical"
        >
          <FlipVertical className="w-3.5 h-3.5" />
          Flip V
        </button>
      </div>

      {/* Sliders Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1">
        {/* Brightness */}
        <div className="space-y-1.5">
          <div className="flex justify-between text-xs font-medium text-slate-600 dark:text-slate-400">
            <span className="flex items-center gap-1"><Sun className="w-3.5 h-3.5 text-amber-500" /> Brightness</span>
            <span className="font-mono">{adjustments.brightness}%</span>
          </div>
          <input
            type="range"
            min="0"
            max="200"
            value={adjustments.brightness}
            onChange={(e) => updateProp('brightness', Number(e.target.value))}
            className="w-full h-1.5 bg-slate-200 dark:bg-slate-800 rounded-lg appearance-none cursor-pointer accent-indigo-600"
          />
        </div>

        {/* Contrast */}
        <div className="space-y-1.5">
          <div className="flex justify-between text-xs font-medium text-slate-600 dark:text-slate-400">
            <span className="flex items-center gap-1"><Contrast className="w-3.5 h-3.5 text-blue-500" /> Contrast</span>
            <span className="font-mono">{adjustments.contrast}%</span>
          </div>
          <input
            type="range"
            min="0"
            max="200"
            value={adjustments.contrast}
            onChange={(e) => updateProp('contrast', Number(e.target.value))}
            className="w-full h-1.5 bg-slate-200 dark:bg-slate-800 rounded-lg appearance-none cursor-pointer accent-indigo-600"
          />
        </div>

        {/* Saturation */}
        <div className="space-y-1.5">
          <div className="flex justify-between text-xs font-medium text-slate-600 dark:text-slate-400">
            <span className="flex items-center gap-1"><Palette className="w-3.5 h-3.5 text-pink-500" /> Saturation</span>
            <span className="font-mono">{adjustments.saturation}%</span>
          </div>
          <input
            type="range"
            min="0"
            max="200"
            value={adjustments.saturation}
            onChange={(e) => updateProp('saturation', Number(e.target.value))}
            className="w-full h-1.5 bg-slate-200 dark:bg-slate-800 rounded-lg appearance-none cursor-pointer accent-indigo-600"
          />
        </div>

        {/* Blur */}
        <div className="space-y-1.5">
          <div className="flex justify-between text-xs font-medium text-slate-600 dark:text-slate-400">
            <span className="flex items-center gap-1"><Eye className="w-3.5 h-3.5 text-purple-500" /> Blur</span>
            <span className="font-mono">{adjustments.blur}px</span>
          </div>
          <input
            type="range"
            min="0"
            max="20"
            value={adjustments.blur}
            onChange={(e) => updateProp('blur', Number(e.target.value))}
            className="w-full h-1.5 bg-slate-200 dark:bg-slate-800 rounded-lg appearance-none cursor-pointer accent-indigo-600"
          />
        </div>
      </div>

      {/* Preset Filter Toggles */}
      <div className="flex items-center gap-3 pt-2 border-t border-slate-100 dark:border-slate-800">
        <label className="text-xs font-medium text-slate-600 dark:text-slate-400">Color Presets:</label>
        <button
          type="button"
          onClick={() => updateProp('grayscale', !adjustments.grayscale)}
          className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
            adjustments.grayscale
              ? 'border-indigo-600 bg-indigo-600 text-white'
              : 'border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100'
          }`}
        >
          B&W Grayscale
        </button>
        <button
          type="button"
          onClick={() => updateProp('sepia', !adjustments.sepia)}
          className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
            adjustments.sepia
              ? 'border-amber-600 bg-amber-600 text-white'
              : 'border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100'
          }`}
        >
          Vintage Sepia
        </button>
      </div>
    </div>
  );
}
